/**
 * Зона доставки: название, цена и контур на карте.
 *
 * Контур правится не здесь, а в окне на весь экран (`VendorZoneMap`): в форме
 * остаётся только превью и кнопка. Карта размером в треть экрана внутри
 * прокручиваемой страницы не годится для разметки района — страница уезжает
 * под пальцем, а район не виден целиком.
 *
 * Самопересечение проверяется ДО отправки: `CHECK ST_IsValid` в базе такой
 * контур отвергнет, но его ответ ничего не объясняет, а на негодном полигоне
 * `ST_Contains` возвращает мусор — зона начинает накрывать не те адреса.
 */
import { Suspense, lazy, useState } from 'react';
import { polygonAreaKm2, type CurrentUser, type GeoJsonPolygon, type VendorZone } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { formatArea, makeT } from '../i18n';
import { haptic } from '../tg';
import { dangerButton, secondaryButton } from './courierUi';
import { polygonSelfIntersects } from './zoneGeometry';
import { VendorZoneMap } from './VendorZoneMap';
import {
  EmptyState,
  Field,
  Toggle,
  bottomBarStyle,
  errorTextStyle,
  hintStyle,
  pageWithBottomBar,
} from './vendorUi';

/** Карта тянет Leaflet — грузим только когда контур действительно показывают. */
const ZonePreview = lazy(async () => {
  await import('@arbuz/ui-map/styles.css');
  const m = await import('@arbuz/ui-map');
  return { default: m.ZonePreview };
});

interface Props {
  user: CurrentUser;
  /** null — создаём новую зону. */
  zone: VendorZone | null;
  onSaved: () => void;
  onBack: () => void;
}

export function VendorZoneEdit({ user, zone, onSaved, onBack }: Props) {
  const t = makeT(user.locale);

  const [name, setName] = useState(zone?.name ?? '');
  const [price, setPrice] = useState(zone ? String(Math.round(Number(zone.delivery_price))) : '');
  const [active, setActive] = useState(zone?.is_active ?? true);
  const [geom, setGeom] = useState<GeoJsonPolygon | null>(zone?.geom ?? null);
  const [mapOpen, setMapOpen] = useState(false);

  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Замыкающая точка дублирует первую — на счёт её не берём
  const pointsCount = geom ? Math.max((geom.coordinates[0]?.length ?? 1) - 1, 0) : 0;

  async function save() {
    setFields({});

    if (!geom) {
      setFields({ geom: t('zoneNeedPoints') });
      haptic('error');
      return;
    }

    if (await polygonSelfIntersects(geom)) {
      setFields({ geom: t('zoneMapCrossing') });
      haptic('error');
      return;
    }

    setSaving(true);
    const body = {
      name: name.trim() || undefined,
      delivery_price: price.includes('.') ? price : `${price || '0'}.00`,
      is_active: active,
      geom,
    };

    try {
      if (zone) await api.patch<VendorZone>(`/api/vendor/zones/${zone.id}`, body);
      else await api.post<VendorZone>('/api/vendor/zones', body);

      haptic('success');
      onSaved();
    } catch (err) {
      haptic('error');
      setFields(err instanceof ApiError ? (err.fields ?? { _: err.message }) : { _: t('actionFailed') });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!zone) return;
    if (!window.confirm(t('zoneRemoveConfirm'))) return;

    setSaving(true);
    try {
      await api.delete(`/api/vendor/zones/${zone.id}`);
      haptic('warning');
      onSaved();
    } catch (err) {
      haptic('error');
      // По зоне с заказами удаление запрещено — предлагаем то, что можно
      setFields({ _: err instanceof ApiError ? err.message : t('zoneHasOrders') });
    } finally {
      setSaving(false);
    }
  }

  const openMap = () => {
    haptic('light');
    setMapOpen(true);
  };

  return (
    <main className="page" style={pageWithBottomBar}>
      <h1 className="h1">{t('zoneEditTitle')}</h1>

      <div style={{ display: 'grid', gap: 'var(--s-4)', marginTop: 'var(--s-4)' }}>
        <Field id="z-name" label={t('zoneNameLabel')} hint={t('zoneNameHint')} error={fields.name}>
          {(p) => (
            <input {...p} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          )}
        </Field>

        <Field id="z-price" label={t('zonePriceLabel')} error={fields.delivery_price}>
          {(p) => (
            <input
              {...p}
              value={price}
              onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))}
              inputMode="numeric"
            />
          )}
        </Field>

        <Toggle label={t('zoneActive')} checked={active} onChange={setActive} />

        <div>
          <p style={hintStyle}>{t('zonePreviewHint')}</p>

          {geom ? (
            <>
              {/* Превью само жестов не принимает — тап достаётся кнопке */}
              <button type="button" onClick={openMap} style={previewButton}>
                <Suspense fallback={<p className="muted">{t('mapLoading')}</p>}>
                  <ZonePreview value={geom} />
                </Suspense>
              </button>
              <p style={{ ...hintStyle, margin: 'var(--s-2) 0 0' }}>
                {pointsCount} {t('zonePoints')} · {formatArea(polygonAreaKm2(geom), user.locale)}
              </p>
              <button type="button" style={secondaryButton} onClick={openMap}>
                {t('zoneMapOpen')}
              </button>
            </>
          ) : (
            <EmptyState
              title={t('zonePreviewEmpty')}
              text={t('zonesEmptyHint')}
              actionLabel={t('zoneMapDraw')}
              onAction={openMap}
            />
          )}

          {fields.geom && (
            <p role="alert" style={errorTextStyle}>
              {fields.geom}
            </p>
          )}
        </div>

        {fields._ && (
          <p role="alert" style={errorTextStyle}>
            {fields._}
          </p>
        )}

        {zone && (
          <button type="button" style={dangerButton} disabled={saving} onClick={() => void remove()}>
            {t('zoneRemove')}
          </button>
        )}
      </div>

      <div style={bottomBarStyle}>
        <button
          type="button"
          className="btn-primary"
          disabled={saving || !price.trim()}
          data-loading={saving ? 'true' : undefined}
          onClick={() => void save()}
        >
          {t('zoneSave')}
        </button>
        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('back')}
        </button>
      </div>

      {mapOpen && (
        <VendorZoneMap
          user={user}
          value={geom}
          onDone={(next) => {
            setGeom(next);
            setFields((prev) => ({ ...prev, geom: '' }));
            setMapOpen(false);
          }}
          onCancel={() => setMapOpen(false)}
        />
      )}
    </main>
  );
}

const previewButton: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: 0,
  border: '1px solid var(--c-border)',
  borderRadius: 'var(--r-md)',
  overflow: 'hidden',
  background: 'var(--c-surface)',
  cursor: 'pointer',
};
